// M12.10 — per-property dismiss state for ExecutiveSummary items.
//
// Persisted in localStorage keyed by property id so a user with two
// connected GA4 properties can dismiss different recommendation
// categories on each. Versioned key (`v1`) lets us rev the schema
// later without eating stale state.
//
// Dashboard-only: the PDF export and digest email always render the
// full list. SharedReportPage doesn't pass a propertyId, which
// disables the dismiss UI for viewers (they can't mutate the owner's
// experience anyway).

import { useCallback, useEffect, useState } from "react";

const STORAGE_PREFIX = "ts:dismissed-recs:v1:";

function storageKey(propertyId: string): string {
  return `${STORAGE_PREFIX}${propertyId}`;
}

function readDismissed(propertyId: string): string[] {
  if (typeof window === "undefined") return [];
  try {
    const raw = window.localStorage.getItem(storageKey(propertyId));
    if (!raw) return [];
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter((x): x is string => typeof x === "string");
  } catch {
    return [];
  }
}

function writeDismissed(propertyId: string, dismissed: string[]): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(storageKey(propertyId), JSON.stringify(dismissed));
  } catch {
    // Quota exceeded / privacy-mode browsers refuse writes. Silent fail
    // is fine — the in-memory state still reflects the user's choice
    // for this session.
  }
}

export interface DismissedRecommendationsApi {
  /** Categories the user has dismissed for the current property. */
  dismissed: ReadonlySet<string>;
  /** Convenience counter so callers can render "N hidden" without
   *  iterating the Set themselves. */
  hiddenCount: number;
  /** Hide a single category for the current property. */
  dismiss: (category: string) => void;
  /** Clear every dismissal for the current property. */
  restoreAll: () => void;
  /** True when dismissal is wired up — false when propertyId is null
   *  (eg. the shared viewer) so the UI can suppress the affordance. */
  enabled: boolean;
}

export function useDismissedRecommendations(
  propertyId: string | null | undefined,
): DismissedRecommendationsApi {
  const [dismissed, setDismissed] = useState<string[]>(() =>
    propertyId ? readDismissed(propertyId) : [],
  );

  // Swap state when the property changes (users switching between
  // connected properties). useState's initializer only runs once, so
  // this effect handles subsequent property changes.
  useEffect(() => {
    setDismissed(propertyId ? readDismissed(propertyId) : []);
  }, [propertyId]);

  const dismiss = useCallback(
    (category: string) => {
      if (!propertyId) return;
      setDismissed((prev) => {
        if (prev.includes(category)) return prev;
        const next = [...prev, category];
        writeDismissed(propertyId, next);
        return next;
      });
    },
    [propertyId],
  );

  const restoreAll = useCallback(() => {
    if (!propertyId) return;
    setDismissed([]);
    writeDismissed(propertyId, []);
  }, [propertyId]);

  return {
    dismissed: new Set(dismissed),
    hiddenCount: dismissed.length,
    dismiss,
    restoreAll,
    enabled: Boolean(propertyId),
  };
}
